import { useEffect, useState } from "react";
import api, { extractErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Search, ShoppingCart } from "lucide-react";

function fmt(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

export default function SuperAbandonedCarts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [draft, setDraft] = useState("");

  const load = (nextQ = q) => {
    setLoading(true);
    const params = { limit: 400 };
    if (nextQ) params.q = nextQ;
    api.get("/platform/abandoned-carts", { params })
      .then(({ data }) => setItems(data.items || []))
      .catch((e) => toast.error(extractErr(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(""); }, []);

  return (
    <div className="space-y-6 max-w-6xl" data-testid="abandoned-cart-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-100">Abandoned Cart</h1>
          <p className="text-slate-500 text-sm mt-1">
            People who filled the signup checkout form and left without paying.
          </p>
        </div>
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => { e.preventDefault(); const next = draft.trim(); setQ(next); load(next); }}
        >
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Search name, number, email, plan…"
              data-testid="abandoned-cart-search"
              className="bg-slate-900 border-slate-700 text-slate-100 pl-8 w-64 h-9 text-sm"
            />
          </div>
        </form>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <Card className="bg-slate-900 border-slate-800 p-10 text-center">
          <ShoppingCart className="w-8 h-8 text-slate-600 mx-auto" />
          <p className="text-slate-400 mt-3 text-sm">{q ? "No matches." : "No abandoned checkouts yet."}</p>
        </Card>
      ) : (
        <div className="rounded-xl border border-slate-800 overflow-hidden bg-[#0f1623]">
          <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2.5 bg-slate-900/80 border-b border-slate-800 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <div className="col-span-3">Name</div>
            <div className="col-span-2">Number</div>
            <div className="col-span-3">Email</div>
            <div className="col-span-2">Plan selected</div>
            <div className="col-span-2">Left</div>
          </div>
          <div className="divide-y divide-slate-800/80">
            {items.map((it) => (
              <div key={it.email} className="grid grid-cols-1 md:grid-cols-12 gap-1 md:gap-3 px-4 py-3.5" data-testid="abandoned-cart-row">
                <div className="md:col-span-3 min-w-0">
                  <div className="md:hidden text-[10px] uppercase tracking-wider text-slate-500">Name</div>
                  <div className="font-semibold text-white text-sm truncate">{it.name || "—"}</div>
                  {it.store_name ? <div className="text-[11px] text-slate-500 truncate">{it.store_name}</div> : null}
                </div>
                <div className="md:col-span-2 min-w-0">
                  <div className="md:hidden text-[10px] uppercase tracking-wider text-slate-500">Number</div>
                  {it.phone ? (
                    <a href={`tel:+91${it.phone}`} className="text-sm text-emerald-300 hover:text-emerald-200">+91 {it.phone}</a>
                  ) : <span className="text-sm text-slate-500">—</span>}
                </div>
                <div className="md:col-span-3 min-w-0">
                  <div className="md:hidden text-[10px] uppercase tracking-wider text-slate-500">Email</div>
                  <a href={`mailto:${it.email}`} className="text-sm text-slate-200 hover:text-white truncate block">{it.email || "—"}</a>
                </div>
                <div className="md:col-span-2 min-w-0">
                  <div className="md:hidden text-[10px] uppercase tracking-wider text-slate-500">Plan selected</div>
                  <div className="text-sm text-indigo-200" data-testid="abandoned-cart-plan">{it.plan_label || "—"}</div>
                </div>
                <div className="md:col-span-2 text-sm text-slate-400">{fmt(it.created_at)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
